import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandInfo, featuredArticles } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9s91nme3oq4jqumu7b0';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim()) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('phone_alt');

    if (!formUrl) {
      setFormError('表单服务暂不可用，请拨打电话400-888-6666联系我们');
      setFormStatus('error');
      return;
    }

    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormStatus('success');
          form.reset();
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          setFormError(msg);
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于被护专家',
    description: brandInfo.description,
    url: 'https://beiziganxi.com/about',
    about: {
      '@type': 'Organization',
      name: '被护专家',
      description: brandInfo.tagline,
      telephone: brandInfo.contact.phone,
      email: brandInfo.contact.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '碧波路888号',
        addressLocality: '上海',
        addressRegion: '浦东新区',
        addressCountry: 'CN',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 | 品牌介绍与联系方式"
        description={brandInfo.description}
        keywords="被护专家,专业干洗,品牌介绍,干洗服务商,联系我们"
        canonicalPath="/about"
        ogImage="https://readdy.ai/api/search-image?query=Professional%20dry%20cleaning%20brand%20storefront%20with%20warm%20inviting%20atmosphere%2C%20clean%20modern%20interior%20with%20textile%20care%20equipment%2C%20Chinese%20professional%20service%20business%2C%20warm%20cream%20and%20wood%20tones%2C%20trustworthy%20brand%20image%2C%20commercial%20photography&width=1200&height=630&seq=about-og-01&orientation=landscape"
        schema={schema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Brand Intro */}
        <section className="bg-background-100 py-16">
          <div className="container-main">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading">
                关于被护专家
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {brandInfo.description}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
              {brandInfo.features.map((feat, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-50 text-accent-600 rounded-xl">
                    <i className={`${feat.icon} text-xl`}></i>
                  </div>
                  <div className="text-lg font-bold text-foreground-900 mb-1 font-heading">{feat.title}</div>
                  <p className="text-xs text-foreground-500">{feat.desc}</p>
                </div>
              ))}
            </div>

            {/* Brand Story */}
            <div className="max-w-3xl mx-auto bg-background-50 border border-background-200/70 rounded-xl p-8">
              <h2 className="text-lg font-bold text-foreground-900 mb-4 font-heading">品牌故事</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {brandInfo.story}
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-background-50">
          <div className="container-main">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">来自真实用户的评价与信赖</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {brandInfo.testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-semibold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Articles */}
        <section className="py-16 bg-background-100">
          <div className="container-main">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-2">推荐阅读</h2>
              <p className="text-sm text-foreground-600">了解更多被芯护理知识</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {featuredArticles.slice(0, 3).map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 no-underline cursor-pointer"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{article.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 bg-background-50">
          <div className="container-main">
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl font-bold text-foreground-900 mb-6 font-heading">联系我们</h2>
                <div className="flex flex-col gap-4 text-sm text-foreground-600">
                  <a href="tel:4008886666" className="flex items-center gap-3 hover:text-primary-600 transition-colors no-underline">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">电话</div>
                      <div className="font-medium text-foreground-900">400-888-6666</div>
                    </div>
                  </a>
                  <a href="mailto:service@beizhuanjia.com" className="flex items-center gap-3 hover:text-primary-600 transition-colors no-underline">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">邮箱</div>
                      <div className="font-medium text-foreground-900">service@beizhuanjia.com</div>
                    </div>
                  </a>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">地址</div>
                      <div className="font-medium text-foreground-900">{brandInfo.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 text-primary-600 rounded-lg">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">营业时间</div>
                      <div className="font-medium text-foreground-900">{brandInfo.contact.hours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 border border-background-200/70 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-foreground-900 mb-1 font-heading">在线咨询</h3>
                  <p className="text-sm text-foreground-500 mb-5">填写以下信息，我们的客服将在2小时内与您联系</p>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="电子邮箱"
                      required
                      className="px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="手机号码"
                      className="px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    />
                    <select
                      name="service_type"
                      className="px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-300 transition-colors"
                    >
                      <option value="">请选择服务类型</option>
                      <option value="蚕丝被干洗">蚕丝被干洗</option>
                      <option value="羽绒被干洗">羽绒被干洗</option>
                      <option value="美女被干洗">美女被干洗</option>
                      <option value="棉被清洁">棉被清洁</option>
                      <option value="毛毯清洗">毛毯清洗</option>
                      <option value="被芯保养">被芯保养</option>
                      <option value="其他">其他</option>
                    </select>
                    <textarea
                      name="message"
                      placeholder="请描述您的需求..."
                      rows={3}
                      maxLength={500}
                      className="px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>

                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-lg text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-send-plane-line mr-2"></i>提交咨询
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 font-medium">
                        <i className="ri-check-line mr-1"></i>提交成功！我们将在2小时内与您联系。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 font-medium">
                        <i className="ri-error-warning-line mr-1"></i>{formError || '提交失败，请稍后重试'}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}